import { FormProvider, getFormProps } from '@conform-to/react'
import { Button, Flex, Heading, Text, Link as RLink } from '@radix-ui/themes'
import { Link, useLocation } from '@remix-run/react'
import * as React from 'react'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { TextField } from '#app/components/ui/text-field.tsx'
import { type Locale } from '#app/models/locale.ts'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	ROOT_APP_VIEW,
	type FormEnum,
} from '#app/routes/_layout/route-config.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useResetPasswordForm } from '#app/utils/hooks/forms/use-reset-password-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Reset password form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	email = 'email',
}

const FORM: FormEnum = {
	id: 'reset-password-form',
	action: '/reset-password',
	fetcherKey: 'reset-password',
	subaction: 'reset-password',
	redirect: '/login',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.email]: z
			.string({ required_error: 'validation.email.required' })
			.email('validation.email.email')
			.max(254, 'validation.email.max'),
	},
})

type ResetPasswordFormProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
	app,
	locale,
	setApp,
}) => {
	const {
		resetPasswordFetcher,
		resetPasswordFormValues,
		resetPasswordForm,
		resetPasswordFields,
		t,
	} = useResetPasswordForm({ app })

	const location = useLocation()

	return (
		<FormProvider context={resetPasswordForm.context}>
			<resetPasswordFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}${location.search}`}
				{...getFormProps(resetPasswordForm)}
				{...(app.options?.preventScrollReset && { preventScrollReset: true })}
			>
				<FormDefaults
					form={resetPasswordForm}
					fields={resetPasswordFields}
					values={resetPasswordFormValues}
				/>
				<Heading align="center" size="8" color="green">
					{t('common.title')}
				</Heading>
				<Flex direction="column" gapY="6" mt="6">
					<TextField
						meta={resetPasswordFields[INPUTS.email]}
						type="email"
						t={t}
					>
						{t('label.email')}
					</TextField>
					<Flex direction="column" gapY="3" mt="3">
						<Flex justify="end">
							<Link
								to={`/${locale}/login${resetPasswordFields[INPUTS['email']].value ? `?email=${resetPasswordFields[INPUTS['email']].value}` : ''}`}
								prefetch="viewport"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: ROOT_APP_VIEW.loginView,
											options: {
												...prev.options,
												data: {
													[INPUTS.email]:
														resetPasswordFields[INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.login')}</span>
								</RLink>
							</Link>
						</Flex>
						<Button size="3" type="submit">
							<SubmissionButton fetcher={resetPasswordFetcher}>
								{t('button.resetPasswordSubmit')}
							</SubmissionButton>
						</Button>
						<Flex align="center" justify="center" gapX="2" mt="4">
							<Text color="gray">{t('common.registration')}</Text>
							<Link
								to={`/${locale}/registration${resetPasswordFields[INPUTS['email']].value ? `?email=${resetPasswordFields[INPUTS['email']].value}` : ''}`}
								prefetch="viewport"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: ROOT_APP_VIEW.registrationView,
											options: {
												...prev.options,
												data: {
													[INPUTS.email]:
														resetPasswordFields[INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.registration')}</span>
								</RLink>
							</Link>
						</Flex>
					</Flex>
				</Flex>
			</resetPasswordFetcher.Form>
		</FormProvider>
	)
}
ResetPasswordForm.displayName = 'ResetPasswordForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type ResetPasswordFormProps,
	ResetPasswordForm,
	INPUTS as RESET_PASSWORD_INPUTS,
	FORM as RESET_PASSWORD_FORM,
	schema as resetPasswordSchema,
}
